import { Card, Col, Row, Table, Tag, Select } from "antd";
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Bar } from "@ant-design/charts";
import checkTokenExp from "../../../utils/checkTokenExp";

const { Option } = Select;

const Dashboard = () => {
  const [clients, setClients] = useState(0);
  const [projects, setProjects] = useState(0);
  const [backlogs, setBacklogs] = useState(0);
  const [sprints, setSprints] = useState(0);
  const [projectList, setProjectList] = useState([]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  checkTokenExp(accessToken);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const clientRes = await axios.get("https://rhrmapi.up2dateinc.com/api/clients", {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const projectRes = await axios.get("https://rhrmapi.up2dateinc.com/api/projects", {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const backlogRes = await axios.get("https://rhrmapi.up2dateinc.com/api/tasks", {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const sprintRes = await axios.get("https://rhrmapi.up2dateinc.com/api/sprints", {
          headers: { Authorization: `Bearer ${accessToken}` }
        });

        setClients(Array.isArray(clientRes.data) ? clientRes.data.length : 0);
        setProjects(Array.isArray(projectRes.data) ? projectRes.data.length : 0);
        setBacklogs(Array.isArray(backlogRes.data) ? backlogRes.data.length : 0);
        setSprints(Array.isArray(sprintRes.data) ? sprintRes.data.length : 0);

        setProjectList(Array.isArray(projectRes.data) ? projectRes.data : []);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        setClients(0);
        setProjects(0);
        setBacklogs(0);
        setSprints(0);
        setProjectList([]);
      }
    };

    fetchData();
  }, [accessToken]);

  if (!isLogged) {
    return <Navigate to={"/admin/auth/login"} replace={true} />;
  }

  // UPDATE STATUS
  const handleStatusChange = async (value, record) => {
    try {
      await axios.put(
        `https://rhrmapi.up2dateinc.com/api/projects/${record._id}`,
        { status: value },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const updatedProjects = projectList.map((proj) =>
        proj._id === record._id ? { ...proj, status: value } : proj
      );
      setProjectList(updatedProjects);
    } catch (error) {
      console.error("Failed to update project status:", error);
    }
  };

  // STATUS COLOR
  const getColor = (status) => {
    if (status === "Finished") return "green";
    if (status === "In Progress") return "blue";
    return "orange";
  };

  // TABLE COLUMNS
  const columns = [
    { title: "Project Name", dataIndex: "projectName", key: "projectName" },
    { title: "Client", dataIndex: "client", key: "client", render: (client) => client || "N/A" },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Select
          value={record.status || "Not Started"}
          style={{ width: 150 }}
          onChange={(value) => handleStatusChange(value, record)}
        >
          <Option value="Not Started">Not Started</Option>
          <Option value="In Progress">In Progress</Option>
          <Option value="Finished">Finished</Option>
        </Select>
      ),
    },
    {
      title: "Indicator",
      render: (_, record) => <Tag color={getColor(record.status)}>{record.status || "Not Started"}</Tag>,
    },
  ];

  // BAR CHART DATA
  const statusData = [
    {
      type: "Not Started",
      value: projectList.filter((p) => !p.status || p.status === "Not Started").length,
    },
    {
      type: "In Progress",
      value: projectList.filter((p) => p.status === "In Progress").length,
    },
    {
      type: "Finished",
      value: projectList.filter((p) => p.status === "Finished").length,
    },
  ];

  // BAR CHART CONFIG
  const barConfig = {
    data: statusData,
    xField: "value",
    yField: "type",
    seriesField: "type",
    legend: false,
    height: 300,
    xAxis: { title: { text: "Number of Projects" } },
    yAxis: { title: { text: "Project Status" } },
    label: { position: "right" },
  };

  return (
    <div>
      {/* DASHBOARD CARDS */}
      <Row gutter={[20, 20]}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="Total Clients">
            <h2>{clients}</h2>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="Total Projects">
            <h2>{projects}</h2>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="Total Backlogs">
            <h2>{backlogs}</h2>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <Card title="Total Sprints">
            <h2>{sprints}</h2>
          </Card>
        </Col>
      </Row>

      {/* PROJECT TABLE */}
      <Row style={{ marginTop: 30 }}>
        <Col span={24}>
          <Card title="Projects Overview">
            <Table
              columns={columns}
              dataSource={Array.isArray(projectList) ? projectList : []}
              rowKey="_id"
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </Col>
      </Row>

      {/* BAR CHART */}
      <Row style={{ marginTop: 30 }}>
        <Col xs={24} md={12}>
          <Card title="Project Status Chart">
            <Bar {...barConfig} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;